import { useEffect, useMemo, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import useBoardStore from '../store/boardStore';
import BoardTable from '../components/board/BoardTable';
import BoardToolbar from '../components/board/BoardToolbar';
import BoardMembers from '../components/board/BoardMembers';
import InviteMembersModal from '../components/board/InviteMembersModal';
import CardPanel from '../components/board/panel/CardPanel';
import BoardUnlockGate from '../components/board/BoardUnlockGate';
import { Lock, Unlock, UserPlus, ShieldAlert } from 'lucide-react';
import { Link } from 'react-router-dom';
import useBoardCan from '../hooks/useBoardCan';
import useAuthStore from '../store/authStore';
import { isBoardUnlocked } from '../utils/boardUnlock';

function EditableText({ value, onSave, editable = true, className = '', inputClassName = '' }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => { setText(value); }, [value]);
  useEffect(() => {
    if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
  }, [editing]);

  const save = () => {
    const trimmed = text.trim();
    if (trimmed && trimmed !== value) onSave(trimmed);
    else setText(value);
    setEditing(false);
  };

  if (editing && editable) {
    return (
      <input
        ref={inputRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') { setText(value); setEditing(false); }
        }}
        className={`bg-transparent border border-accent/50 rounded px-2 py-0.5 outline-none text-text-primary ${inputClassName}`}
      />
    );
  }

  return (
    <span
      onClick={() => editable && setEditing(true)}
      className={`rounded px-2 py-0.5 transition-colors ${editable ? 'cursor-pointer hover:bg-white/5' : ''} ${className}`}
    >
      {value}
    </span>
  );
}

export default function BoardView() {
  const { id } = useParams();
  const { currentBoard, loadingBoard, editMode, boardError } = useBoardStore(
    useShallow((s) => ({
      currentBoard: s.currentBoard,
      loadingBoard: s.loadingBoard,
      editMode: s.editMode,
      boardError: s.boardError,
    }))
  );
  const fetchBoardData = useBoardStore((s) => s.fetchBoardData);
  const fetchBoardMembers = useBoardStore((s) => s.fetchBoardMembers);
  const updateBoard = useBoardStore((s) => s.updateBoard);
  const toggleEditMode = useBoardStore((s) => s.toggleEditMode);
  const startPolling = useBoardStore((s) => s.startPolling);
  const stopPolling = useBoardStore((s) => s.stopPolling);
  const can = useBoardCan();
  const user = useAuthStore((s) => s.user);
  const [filters, setFilters] = useState({ search: '', person: null, status: {} });
  const [hiddenColumns, setHiddenColumns] = useState(() => new Set());
  const [sort, setSort] = useState({ columnId: null, dir: 'asc' });
  const [groupBy, setGroupBy] = useState('group');
  const [inviteOpen, setInviteOpen] = useState(false);
  // Nonce de desbloqueio: muda quando a senha e aceita, so pra este componente
  // reler o sessionStorage. Nao guarda "esta desbloqueado" — isso e DERIVADO
  // da trinca quadro+usuario+geracao abaixo, e derivar e o que mantem as
  // quatro formas de invalidar funcionando sem efeito nenhum:
  //   outro quadro        -> currentBoard.id muda
  //   outro usuario       -> user.id muda (sair e entrar na mesma aba)
  //   senha trocada aqui  -> setBoardProtection poe a geracao nova na store
  //   senha trocada fora  -> a geracao nova chega pelo polling
  // Guardar num estado booleano reintroduziria justamente o caso de outra aba,
  // que nenhum efeito local enxerga.
  const [unlockNonce, setUnlockNonce] = useState(0);
  const unlocked = useMemo(
    () => isBoardUnlocked(currentBoard?.id, user?.id, currentBoard?.protection_version),
    [currentBoard?.id, currentBoard?.protection_version, user?.id, unlockNonce]
  );

  const toggleColumnHidden = (colId) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (next.has(colId)) next.delete(colId);
      else next.add(colId);
      return next;
    });
  };

  useEffect(() => {
    setFilters({ search: '', person: null, status: {} });
    if (id) {
      fetchBoardData(id);
      fetchBoardMembers(id);
      startPolling(id, 5000);
    }
    return () => stopPolling();
  }, [id]);

  if (loadingBoard) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (boardError?.status === 403) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="text-center max-w-md">
          <div className="w-14 h-14 rounded-full bg-danger/10 flex items-center justify-center mx-auto mb-4">
            <ShieldAlert className="w-7 h-7 text-danger" />
          </div>
          <h2 className="text-lg font-semibold text-text-primary mb-1">Sem acesso ao quadro</h2>
          <p className="text-sm text-text-secondary mb-5">
            Você não tem permissão para visualizar este quadro. Peça a um administrador para te adicionar.
          </p>
          <Link
            to="/"
            className="inline-block px-4 py-2 bg-accent text-white text-sm rounded hover:bg-accent/80 transition-colors"
          >
            Voltar ao início
          </Link>
        </div>
      </div>
    );
  }

  if (!currentBoard) {
    return (
      <div className="flex items-center justify-center h-full text-text-secondary">
        Board não encontrado
      </div>
    );
  }

  // Quadro protegido e ainda nao desbloqueado nesta aba, por este usuario, na
  // geracao atual da senha: o conteudo nao e montado. `is_protected` ausente
  // (backend antigo) conta como nao protegido.
  const locked = !!currentBoard.is_protected && !unlocked;

  return (
    <div className="h-full flex flex-col">
      <div className="px-8 pt-6 pb-3 border-b border-border/30">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <EditableText
              value={currentBoard.name}
              onSave={(name) => updateBoard(currentBoard.id, { name })}
              editable={can.board_settings}
              className="text-2xl font-bold text-text-primary"
              inputClassName="text-2xl font-bold"
            />
            {/* Indicador de quadro protegido. Texto junto do cadeado de
                proposito: o botao de layout ao lado usa o MESMO icone com
                outro sentido (travar colunas), e so o rotulo distingue os
                dois. Diz o que a feature faz — pede senha —, nao mais. */}
            {currentBoard.is_protected && (
              <span
                className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent/10 border border-accent/30 text-[11px] font-medium text-accent"
                title="Pede senha para abrir"
              >
                <Lock className="w-3 h-3" />
                Pede senha
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {can.edit_structure && (
              <button
                onClick={toggleEditMode}
                className={`p-2 rounded-md transition-colors ${editMode ? 'bg-accent/20 text-accent hover:bg-accent/30' : 'text-text-secondary hover:text-text-primary hover:bg-white/5'}`}
                title={editMode ? 'Modo de edição ativo — clique para travar o layout' : 'Desbloquear layout para editar colunas'}
              >
                {editMode ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
              </button>
            )}
            <BoardMembers />
            {can.manage_access && (
              <button
                onClick={() => setInviteOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-text-primary hover:bg-white/5 border border-border/50 transition-colors"
                title="Gerenciar acesso ao quadro"
              >
                <UserPlus className="w-4 h-4" />
                Acesso ao quadro
              </button>
            )}
          </div>
        </div>
        {!locked && (
          <div className="mt-4">
            <BoardToolbar
              filters={filters}
              setFilters={setFilters}
              hiddenColumns={hiddenColumns}
              toggleColumnHidden={toggleColumnHidden}
              sort={sort}
              setSort={setSort}
              groupBy={groupBy}
              setGroupBy={setGroupBy}
            />
          </div>
        )}
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">
        {/* O pedido de senha fica ACIMA da BoardTable, aqui na BoardView, e
            no lugar dela: enquanto o quadro esta travado a tabela nao e
            montada. Nada de gate por dentro da tabela. */}
        {locked ? (
          <BoardUnlockGate board={currentBoard} onUnlocked={() => setUnlockNonce((n) => n + 1)} />
        ) : (
          <BoardTable
            filters={filters}
            hiddenColumns={hiddenColumns}
            sort={sort}
            groupBy={groupBy}
          />
        )}
      </div>
      {!locked && <CardPanel />}
      {inviteOpen && <InviteMembersModal onClose={() => setInviteOpen(false)} />}
    </div>
  );
}
